import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  Radio,
  Switch,
  InputNumber,
  message,
  Popconfirm,
  Divider,
  Tabs,
  ColorPicker,
  Descriptions,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  TagOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

// 标签组接口
interface TagGroup {
  id: number;
  name: string;
  description?: string;
  isMutuallyExclusive: boolean; // 是否互斥
  sortOrder: number;
  enabled: boolean;
  tagCount: number; // 标签数量
  creator: string;
  createTime: string;
  updater: string;
  updateTime: string;
}

// 标签接口
interface UserTag {
  id: number;
  name: string;
  description?: string;
  tagType: 'manual' | 'auto'; // 手动标签 / 自动标签
  tagGroupId: number;
  tagGroupName: string;
  color: string; // 标签颜色
  icon?: string; // 标签图标
  enabled: boolean;
  userCount: number; // 用户数量
  autoRule?: AutoTagRule; // 自动标签规则
  creator: string;
  createTime: string;
  updater: string;
  updateTime: string;
}

// 自动标签规则接口
interface AutoTagRule {
  id: number;
  tagId: number;
  ruleName: string;
  conditions: AutoTagCondition[]; // 触发条件
  markLogic: 'and' | 'or'; // 条件逻辑：且/或
  unmarkConditions?: AutoTagCondition[]; // 取消标记条件（可选）
  unmarkLogic?: 'and' | 'or';
}

// 自动标签条件接口
interface AutoTagCondition {
  field: string; // 字段：registerDays, rechargeAmount, betCount, lastLoginDays等
  operator: '>=' | '<=' | '=' | '>' | '<' | 'between' | 'in' | 'notIn';
  value: any; // 值
  value2?: any; // 范围值（用于between）
}

// 模拟数据
const mockTagGroups: TagGroup[] = [
  {
    id: 1,
    name: '用户等级',
    description: '根据用户行为自动标记等级',
    isMutuallyExclusive: true,
    sortOrder: 1,
    enabled: true,
    tagCount: 5,
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 2,
    name: '用户状态',
    description: '用户活跃状态标签',
    isMutuallyExclusive: true,
    sortOrder: 2,
    enabled: true,
    tagCount: 3,
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 3,
    name: '用户特征',
    description: '用户行为特征标签，可多选',
    isMutuallyExclusive: false,
    sortOrder: 3,
    enabled: true,
    tagCount: 8,
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
];

const mockTags: UserTag[] = [
  {
    id: 1,
    name: 'VIP1',
    description: '注册7天以上，充值1000VND以上',
    tagType: 'auto',
    tagGroupId: 1,
    tagGroupName: '用户等级',
    color: '#ff4d4f',
    enabled: true,
    userCount: 1250,
    autoRule: {
      id: 1,
      tagId: 1,
      ruleName: 'VIP1自动标记规则',
      conditions: [
        { field: 'registerDays', operator: '>=', value: 7 },
        { field: 'rechargeAmount', operator: '>=', value: 1000 },
      ],
      markLogic: 'and',
    },
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 2,
    name: 'VIP2',
    description: '注册30天以上，充值10000VND以上',
    tagType: 'auto',
    tagGroupId: 1,
    tagGroupName: '用户等级',
    color: '#ff7a45',
    enabled: true,
    userCount: 856,
    autoRule: {
      id: 2,
      tagId: 2,
      ruleName: 'VIP2自动标记规则',
      conditions: [
        { field: 'registerDays', operator: '>=', value: 30 },
        { field: 'rechargeAmount', operator: '>=', value: 10000 },
      ],
      markLogic: 'and',
    },
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 3,
    name: '活跃用户',
    description: '7天内登录过',
    tagType: 'auto',
    tagGroupId: 2,
    tagGroupName: '用户状态',
    color: '#52c41a',
    enabled: true,
    userCount: 3420,
    autoRule: {
      id: 3,
      tagId: 3,
      ruleName: '活跃用户自动标记规则',
      conditions: [
        { field: 'lastLoginDays', operator: '<=', value: 7 },
      ],
      markLogic: 'and',
    },
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 4,
    name: '沉默用户',
    description: '30天未登录',
    tagType: 'auto',
    tagGroupId: 2,
    tagGroupName: '用户状态',
    color: '#8c8c8c',
    enabled: true,
    userCount: 1250,
    autoRule: {
      id: 4,
      tagId: 4,
      ruleName: '沉默用户自动标记规则',
      conditions: [
        { field: 'lastLoginDays', operator: '>=', value: 30 },
      ],
      markLogic: 'and',
    },
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 5,
    name: '高价值用户',
    description: '手动标记的高价值用户',
    tagType: 'manual',
    tagGroupId: 3,
    tagGroupName: '用户特征',
    color: '#1890ff',
    enabled: true,
    userCount: 156,
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 6,
    name: '风险用户',
    description: '手动标记的风险用户',
    tagType: 'manual',
    tagGroupId: 3,
    tagGroupName: '用户特征',
    color: '#faad14',
    enabled: true,
    userCount: 23,
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
];

// 条件字段选项
const CONDITION_FIELDS = [
  { value: 'registerDays', label: '注册天数' },
  { value: 'rechargeAmount', label: '累计充值金额(VND)' },
  { value: 'rechargeCount', label: '充值次数' },
  { value: 'betAmount', label: '累计投注金额(VND)' },
  { value: 'betCount', label: '投注次数' },
  { value: 'winAmount', label: '累计赢取金额(VND)' },
  { value: 'lastLoginDays', label: '距离最后登录天数' },
  { value: 'lastRechargeDays', label: '距离最后充值天数' },
  { value: 'lastBetDays', label: '距离最后投注天数' },
  { value: 'vipLevel', label: 'VIP等级' },
];

// 操作符选项
const OPERATORS = [
  { value: '>=', label: '大于等于' },
  { value: '<=', label: '小于等于' },
  { value: '=', label: '等于' },
  { value: '>', label: '大于' },
  { value: '<', label: '小于' },
  { value: 'between', label: '介于' },
  { value: 'in', label: '包含' },
  { value: 'notIn', label: '不包含' },
];

const UserTagManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [groupForm] = Form.useForm();
  const [tagForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState<'groups' | 'tags'>('groups');
  const [tagGroups, setTagGroups] = useState<TagGroup[]>(mockTagGroups);
  const [tags, setTags] = useState<UserTag[]>(mockTags);
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<TagGroup | null>(null);
  const [editingTag, setEditingTag] = useState<UserTag | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const tagType = Form.useWatch('tagType', tagForm);

  // 标签组管理
  const handleAddGroup = () => {
    setEditingGroup(null);
    groupForm.resetFields();
    groupForm.setFieldsValue({
      enabled: true,
      isMutuallyExclusive: false,
      sortOrder: tagGroups.length + 1,
    });
    setGroupModalVisible(true);
  };

  const handleEditGroup = (group: TagGroup) => {
    setEditingGroup(group);
    groupForm.setFieldsValue(group);
    setGroupModalVisible(true);
  };

  const handleDeleteGroup = (group: TagGroup) => {
    // 检查是否有标签使用该标签组
    const hasTags = tags.some(tag => tag.tagGroupId === group.id);
    if (hasTags) {
      message.warning('该标签组下还有标签，请先删除或移动标签');
      return;
    }
    setTagGroups(prev => prev.filter(item => item.id !== group.id));
    message.success('标签组已删除');
  };

  const handleGroupSubmit = async () => {
    try {
      const values = await groupForm.validateFields();
      const payload: TagGroup = {
        id: editingGroup ? editingGroup.id : Date.now(),
        name: values.name,
        description: values.description,
        isMutuallyExclusive: values.isMutuallyExclusive,
        sortOrder: values.sortOrder,
        enabled: values.enabled,
        tagCount: editingGroup ? editingGroup.tagCount : 0,
        creator: editingGroup ? editingGroup.creator : 'admin',
        createTime: editingGroup ? editingGroup.createTime : dayjs().format('YYYY-MM-DD HH:mm:ss'),
        updater: 'admin',
        updateTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      };

      setTagGroups(prev => {
        if (editingGroup) {
          return prev.map(item => (item.id === editingGroup.id ? payload : item));
        }
        return [payload, ...prev];
      });

      message.success(editingGroup ? '标签组已更新' : '标签组已创建');
      setGroupModalVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // 标签管理
  const handleAddTag = () => {
    setEditingTag(null);
    tagForm.resetFields();
    tagForm.setFieldsValue({
      tagType: 'manual',
      enabled: true,
      color: '#1890ff',
      markLogic: 'and',
    });
    setTagModalVisible(true);
  };

  const handleEditTag = (tag: UserTag) => {
    setEditingTag(tag);
    const values: any = {
      ...tag,
      conditions: tag.autoRule?.conditions || [],
      unmarkConditions: tag.autoRule?.unmarkConditions || [],
      markLogic: tag.autoRule?.markLogic || 'and',
      unmarkLogic: tag.autoRule?.unmarkLogic || 'and',
      ruleName: tag.autoRule?.ruleName || '',
    };
    tagForm.setFieldsValue(values);
    setTagModalVisible(true);
  };

  const handleDeleteTag = (tag: UserTag) => {
    setTags(prev => prev.filter(item => item.id !== tag.id));
    // 更新标签组的标签数量
    setTagGroups(prev => prev.map(group => {
      if (group.id === tag.tagGroupId) {
        return { ...group, tagCount: group.tagCount - 1 };
      }
      return group;
    }));
    message.success('标签已删除');
  };

  const handleTagSubmit = async () => {
    try {
      const values = await tagForm.validateFields();
      const selectedGroup = tagGroups.find(g => g.id === values.tagGroupId);
      
      const payload: UserTag = {
        id: editingTag ? editingTag.id : Date.now(),
        name: values.name,
        description: values.description,
        tagType: values.tagType,
        tagGroupId: values.tagGroupId,
        tagGroupName: selectedGroup?.name || '',
        color: values.color,
        icon: values.icon,
        enabled: values.enabled,
        userCount: editingTag ? editingTag.userCount : 0,
        autoRule: values.tagType === 'auto' ? {
          id: editingTag?.autoRule?.id || Date.now(),
          tagId: editingTag ? editingTag.id : Date.now(),
          ruleName: values.ruleName,
          conditions: values.conditions || [],
          markLogic: values.markLogic || 'and',
          unmarkConditions: values.unmarkConditions,
          unmarkLogic: values.unmarkLogic,
        } : undefined,
        creator: editingTag ? editingTag.creator : 'admin',
        createTime: editingTag ? editingTag.createTime : dayjs().format('YYYY-MM-DD HH:mm:ss'),
        updater: 'admin',
        updateTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      };

      setTags(prev => {
        if (editingTag) {
          return prev.map(item => (item.id === editingTag.id ? payload : item));
        }
        // 新增标签时，更新标签组的标签数量
        setTagGroups(prev => prev.map(group => {
          if (group.id === values.tagGroupId) {
            return { ...group, tagCount: group.tagCount + 1 };
          }
          return group;
        }));
        return [payload, ...prev];
      });

      message.success(editingTag ? '标签已更新' : '标签已创建');
      setTagModalVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const filteredTags = tags.filter(tag => {
    const matchesSearch = !searchText || 
      tag.name.toLowerCase().includes(searchText.toLowerCase()) ||
      tag.description?.toLowerCase().includes(searchText.toLowerCase());
    const matchesGroup = !selectedGroupId || tag.tagGroupId === selectedGroupId;
    return matchesSearch && matchesGroup;
  });

  const groupColumns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '标签组名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: '是否互斥',
      dataIndex: 'isMutuallyExclusive',
      key: 'isMutuallyExclusive',
      width: 100,
      render: (isMutuallyExclusive: boolean) => (
        <Tag color={isMutuallyExclusive ? 'red' : 'blue'}>
          {isMutuallyExclusive ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '标签数量',
      dataIndex: 'tagCount',
      key: 'tagCount',
      width: 100,
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
      sorter: (a: TagGroup, b: TagGroup) => a.sortOrder - b.sortOrder,
    },
    {
      title: '启用状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? '开启' : '关闭'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: TagGroup) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditGroup(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除该标签组？"
            onConfirm={() => handleDeleteGroup(record)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tagColumns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '标签名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (name: string, record: UserTag) => (
        <Space>
          <Tag color={record.color}>{name}</Tag>
          {record.tagType === 'auto' && (
            <Tooltip title="自动标签">
              <Tag color="blue">自动</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: '标签类型',
      dataIndex: 'tagType',
      key: 'tagType',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'auto' ? 'blue' : 'green'}>
          {type === 'auto' ? '自动标签' : '手动标签'}
        </Tag>
      ),
    },
    {
      title: '所属标签组',
      dataIndex: 'tagGroupName',
      key: 'tagGroupName',
      width: 120,
    },
    {
      title: '用户数量',
      dataIndex: 'userCount',
      key: 'userCount',
      width: 100,
      sorter: (a: UserTag, b: UserTag) => a.userCount - b.userCount,
      render: (count: number) => count.toLocaleString(),
    },
    {
      title: '启用状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? '开启' : '关闭'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right' as const,
      render: (_: any, record: UserTag) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditTag(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除该标签？"
            onConfirm={() => handleDeleteTag(record)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderAutoRuleForm = () => {
    if (tagType !== 'auto') return null;

    return (
      <>
        <Divider orientation="left">自动标记规则</Divider>
        <Form.Item
          label="规则名称"
          name="ruleName"
          rules={[{ required: true, message: '请输入规则名称' }]}
        >
          <Input placeholder="请输入规则名称" />
        </Form.Item>

        <Form.Item
          label="标记条件逻辑"
          name="markLogic"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio value="and">且（所有条件都满足）</Radio>
            <Radio value="or">或（任一条件满足）</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.List name="conditions">
          {(fields, { add, remove }) => (
            <>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>标记条件：</div>
              {fields.map(field => (
                <Row key={field.key} gutter={16} style={{ marginBottom: 12 }}>
                  <Col span={6}>
                    <Form.Item
                      name={[field.name, 'field']}
                      rules={[{ required: true, message: '请选择字段' }]}
                    >
                      <Select placeholder="选择字段">
                        {CONDITION_FIELDS.map(f => (
                          <Option key={f.value} value={f.value}>{f.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name={[field.name, 'operator']}
                      rules={[{ required: true, message: '请选择操作符' }]}
                    >
                      <Select placeholder="操作符">
                        {OPERATORS.map(op => (
                          <Option key={op.value} value={op.value}>{op.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[field.name, 'value']}
                      rules={[{ required: true, message: '请输入值' }]}
                    >
                      <InputNumber style={{ width: '100%' }} placeholder="值" />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.conditions?.[field.name]?.operator !== curr.conditions?.[field.name]?.operator}>
                      {({ getFieldValue }) => {
                        const operator = getFieldValue(['conditions', field.name, 'operator']);
                        if (operator === 'between') {
                          return (
                            <Form.Item
                              name={[field.name, 'value2']}
                              rules={[{ required: true, message: '请输入值' }]}
                            >
                              <InputNumber style={{ width: '100%' }} placeholder="最大值" />
                            </Form.Item>
                          );
                        }
                        return null;
                      }}
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Button type="link" danger onClick={() => remove(field.name)}>
                      删除
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button type="dashed" onClick={() => add({ field: '', operator: '>=', value: 0 })} block icon={<PlusOutlined />}>
                添加条件
              </Button>
            </>
          )}
        </Form.List>

        <Divider orientation="left">自动取消标记规则（可选）</Divider>
        <Form.Item
          label="取消标记条件逻辑"
          name="unmarkLogic"
        >
          <Radio.Group>
            <Radio value="and">且（所有条件都满足）</Radio>
            <Radio value="or">或（任一条件满足）</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.List name="unmarkConditions">
          {(fields, { add, remove }) => (
            <>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>取消标记条件：</div>
              {fields.map(field => (
                <Row key={field.key} gutter={16} style={{ marginBottom: 12 }}>
                  <Col span={6}>
                    <Form.Item
                      name={[field.name, 'field']}
                      rules={[{ required: true, message: '请选择字段' }]}
                    >
                      <Select placeholder="选择字段">
                        {CONDITION_FIELDS.map(f => (
                          <Option key={f.value} value={f.value}>{f.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name={[field.name, 'operator']}
                      rules={[{ required: true, message: '请选择操作符' }]}
                    >
                      <Select placeholder="操作符">
                        {OPERATORS.map(op => (
                          <Option key={op.value} value={op.value}>{op.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[field.name, 'value']}
                      rules={[{ required: true, message: '请输入值' }]}
                    >
                      <InputNumber style={{ width: '100%' }} placeholder="值" />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.unmarkConditions?.[field.name]?.operator !== curr.unmarkConditions?.[field.name]?.operator}>
                      {({ getFieldValue }) => {
                        const operator = getFieldValue(['unmarkConditions', field.name, 'operator']);
                        if (operator === 'between') {
                          return (
                            <Form.Item
                              name={[field.name, 'value2']}
                              rules={[{ required: true, message: '请输入值' }]}
                            >
                              <InputNumber style={{ width: '100%' }} placeholder="最大值" />
                            </Form.Item>
                          );
                        }
                        return null;
                      }}
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Button type="link" danger onClick={() => remove(field.name)}>
                      删除
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button type="dashed" onClick={() => add({ field: '', operator: '>=', value: 0 })} block icon={<PlusOutlined />}>
                添加条件
              </Button>
            </>
          )}
        </Form.List>
      </>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="用户标签管理"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => message.info('已刷新')}>
              刷新
            </Button>
          </Space>
        }
      >
        <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as 'groups' | 'tags')}>
          <TabPane tab="标签组管理" key="groups">
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddGroup}>
                  新增标签组
                </Button>
              </Space>
            </div>
            <Table
              columns={groupColumns}
              dataSource={tagGroups}
              rowKey="id"
              pagination={{
                total: tagGroups.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
              }}
            />
          </TabPane>
          <TabPane tab="标签管理" key="tags">
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input
                  placeholder="搜索标签名称或描述"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 300 }}
                  allowClear
                />
                <Select
                  placeholder="筛选标签组"
                  value={selectedGroupId}
                  onChange={setSelectedGroupId}
                  allowClear
                  style={{ width: 200 }}
                >
                  {tagGroups.map(group => (
                    <Option key={group.id} value={group.id}>{group.name}</Option>
                  ))}
                </Select>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTag}>
                  新增标签
                </Button>
              </Space>
            </div>
            <Table
              columns={tagColumns}
              dataSource={filteredTags}
              rowKey="id"
              pagination={{
                total: filteredTags.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
              }}
              scroll={{ x: 1200 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 标签组编辑模态框 */}
      <Modal
        title={editingGroup ? '编辑标签组' : '新增标签组'}
        open={groupModalVisible}
        onCancel={() => setGroupModalVisible(false)}
        onOk={handleGroupSubmit}
        width={600}
      >
        <Form
          form={groupForm}
          layout="vertical"
        >
          <Form.Item
            label="标签组名称"
            name="name"
            rules={[{ required: true, message: '请输入标签组名称' }]}
          >
            <Input placeholder="请输入标签组名称" />
          </Form.Item>
          <Form.Item
            label="描述"
            name="description"
          >
            <TextArea rows={3} placeholder="请输入描述" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="是否互斥"
                name="isMutuallyExclusive"
                valuePropName="checked"
              >
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="排序"
                name="sortOrder"
                rules={[{ required: true, message: '请输入排序' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="启用状态"
            name="enabled"
            valuePropName="checked"
          >
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 标签编辑模态框 */}
      <Modal
        title={editingTag ? '编辑标签' : '新增标签'}
        open={tagModalVisible}
        onCancel={() => setTagModalVisible(false)}
        onOk={handleTagSubmit}
        width={1000}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={tagForm}
          layout="vertical"
          initialValues={{
            tagType: 'manual',
            enabled: true,
            color: '#1890ff',
            markLogic: 'and',
            conditions: [],
            unmarkConditions: [],
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="标签名称"
                name="name"
                rules={[{ required: true, message: '请输入标签名称' }]}
              >
                <Input placeholder="请输入标签名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="所属标签组"
                name="tagGroupId"
                rules={[{ required: true, message: '请选择标签组' }]}
              >
                <Select placeholder="请选择标签组">
                  {tagGroups.map(group => (
                    <Option key={group.id} value={group.id}>{group.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="描述"
            name="description"
          >
            <TextArea rows={2} placeholder="请输入描述" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="标签类型"
                name="tagType"
                rules={[{ required: true, message: '请选择标签类型' }]}
              >
                <Radio.Group>
                  <Radio value="manual">手动标签</Radio>
                  <Radio value="auto">自动标签</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="标签颜色"
                name="color"
                rules={[{ required: true, message: '请选择标签颜色' }]}
              >
                <ColorPicker showText />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="启用状态"
                name="enabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
              </Form.Item>
            </Col>
          </Row>

          {renderAutoRuleForm()}
        </Form>
      </Modal>
    </div>
  );
};

export default UserTagManagement;

